import {Component, HostListener, Injectable, OnInit, ViewChild} from '@angular/core';
import {Machine} from "../../../model/machine";
import {MachineGroup} from "../../../model/machine-group";
import {MachineGroupsService} from "../../../services/machine-groups.service";
import {FirmwareService} from "../../../services/firmware.service";
import {ColorService} from "../../../services/color.service";
import {Firmware} from "../../../model/firmware";
import {ComponentCanDeactivate} from "../../../common/can-deactivate/component-can-deactivate";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {FirmwareRequest} from "../../../model/requests/firmware-request";
import {FirmwareFilters} from "../../../model/filters/firmware-filters";
import {MachineModel} from "../../../model/machine-model";
import {MachineModelsService} from "../../../services/machine-models.service";
import {FirmwareType} from "../../../model/firmware-type";
import {MessageService} from "primeng/api";
import {TranslocoService} from "@ngneat/transloco";

@Component({
  selector: 'app-firmware-list',
  templateUrl: './firmware-list.component.html',
  styleUrls: ['./firmware-list.component.scss']
})
@Injectable({
  providedIn: 'root'
})
export class FirmwareListComponent implements OnInit, ComponentCanDeactivate {

  @ViewChild("table")
  public table: any;

  public readonly NO_ACTION: string = 'noAction';
  public readonly DOWNGRADE: string = 'downgrade';
  public readonly UPGRADE: string = 'upgrade';

  public models: MachineModel[];
  public machines: Machine[];
  public groups: MachineGroup[];
  public firmwareVersionsIot: Firmware[];
  public firmwareVersionsMachineEcu: Firmware[];
  public firmwareVersionsMachineElevator: Firmware[];

  public totalMachines: number;
  public loadingMachines: boolean;
  public loadingFilters: boolean;
  public updatingFirmware: boolean = false;

  public selectedMachines: Machine[] = [];
  public selectedMachinesZitrux: Machine[] = [];
  public selectedMachinesFirmwareIotActions: Map<string, string>;
  public selectedMachinesFirmwareMachineEcuActions: Map<string, string>;
  public selectedMachinesFirmwareMachineElevatorActions: Map<string, string>;
  public selectedVersionIot: Firmware;
  public selectedVersionMachineEcu: Firmware;
  public selectedVersionMachineElevator: Firmware;

  public rows: number = 10;
  public rowsSelected: number = 10;
  public first: number = 0;
  public firstSelected: number = 0;

  public currentFilters: FirmwareFilters;
  public filtersVisible: boolean;
  public accordionMachinesSelected: boolean = true;
  public accordionFirmwareIotSelected: boolean = false;
  public accordionFirmwareMachineSelected: boolean = false;
  public accordionFirmwareElevatorMachineSelected: boolean = false;

  private firmwareVersionsMap: Map<string, number>;


  constructor(private firmwareService: FirmwareService,
              private machineModelsService: MachineModelsService,
              private machineGroupsService: MachineGroupsService,
              private i18n: TranslocoService,
              private commonMessagesService: CommonMessagesService,
              private messageService: MessageService,
              private colorService: ColorService) {
    this.currentFilters = new FirmwareFilters();
  }

  ngOnInit(): void {
    this.loadMachines();
  }

  private loadMachines(): void {
    this.loadingMachines = true;
    this.machines = new Array(this.rows);
    this.selectedMachines = [];
    this.selectedMachinesZitrux = [];
    this.firmwareVersionsIot = [];
    this.firmwareVersionsMachineEcu = [];
    this.firmwareVersionsMachineElevator = [];
    this.selectedMachinesFirmwareIotActions = new Map<string, string>();
    this.selectedMachinesFirmwareMachineEcuActions = new Map<string, string>();
    this.selectedMachinesFirmwareMachineElevatorActions = new Map<string, string>();
    this.firmwareService.getMachinesFirmware().subscribe(response => {
      this.machines = response.machines;
      this.totalMachines = response.machines.length;
      this.loadingMachines = false;
      this.loadFilters();
    });
  }

  private loadFilters(): void {
    this.selectedVersionIot = null;
    this.selectedVersionMachineEcu = null;
    this.selectedVersionMachineElevator = null;
    this.machineModelsService.getModels().subscribe(response => {
      this.models = response.models;
    });
    this.machineGroupsService.getGroups().subscribe(response => {
      this.groups = response.data.content;
    });
    this.firmwareService.getFirmwares().subscribe(response => {
      this.firmwareVersionsMap = new Map<string, number>();
      response.firmwares.forEach(firmwareVersion => {
        this.firmwareVersionsMap.set(firmwareVersion.firmwareVersion, firmwareVersion.value);
        if (!firmwareVersion.disabled) {
          if (firmwareVersion.firmwareType === FirmwareType.FIRMWARE_IOT) {
            this.firmwareVersionsIot.push(firmwareVersion);
          } else if (firmwareVersion.firmwareType === FirmwareType.FIRMWARE_MACHINE_ECU) {
            this.firmwareVersionsMachineEcu.push(firmwareVersion);
          } else {
            this.firmwareVersionsMachineElevator.push(firmwareVersion);
          }
        }
      });
      this.loadingFilters = false;
    });
  }

  adjustTextColor(colorHex): string {
    return this.colorService.adjustTextColor(colorHex);
  }

  public updateSelected(firmwareType: FirmwareType): void {
    this.commonMessagesService.showUpdateFirmwareRequestConfirmation(() => {
      this.updatingFirmware = true;
      let selectedMachinesUuids: string[] = this.getSelectedMachinesUuids(firmwareType);

      if (selectedMachinesUuids.length == 0) {
        this.successActions();
        return;
      }

      let firmwareVersion;
      if (firmwareType === FirmwareType.FIRMWARE_IOT) {
        firmwareVersion = this.selectedVersionIot;
      } else {
        firmwareVersion = firmwareType === FirmwareType.FIRMWARE_MACHINE_ECU ? this.selectedVersionMachineEcu :
            this.selectedVersionMachineElevator;
      }

      let firmwareRequest: FirmwareRequest = {
        machineUuids: selectedMachinesUuids,
        firmwareVersion: firmwareVersion
      }

      this.firmwareService.updateFirmware(firmwareRequest).subscribe(() => {
        this.successActions();
      }, () => {
        this.commonMessagesService.showUnexpectedPersistingError('edit');
        this.updatingFirmware = false;
      });
    });
  }

  private successActions(): void {
    const summary = this.i18n.translate('success.generic.changesApplied');
    const detail = this.i18n.translate('success.firmware.updated');
    this.messageService.add({severity: 'success', summary, detail, sticky: false});

    this.resetFilters();
    this.loadMachines();
    this.updatingFirmware = false;
  }

  private getSelectedMachinesUuids(firmwareType: FirmwareType): string[] {
    let selectedMachinesUuids: string[] = [];

    if (firmwareType === FirmwareType.FIRMWARE_IOT) {
      this.selectedMachines.forEach(machine => {
        if (this.selectedMachinesFirmwareIotActions.get(machine.machineUuid) != this.NO_ACTION) {
          selectedMachinesUuids.push(machine.machineUuid);
        }
      });

    } else {
      this.selectedMachinesZitrux.forEach(machine => {
        if (firmwareType === FirmwareType.FIRMWARE_MACHINE_ECU) {
          if (this.selectedMachinesFirmwareMachineEcuActions.get(machine.machineUuid) != this.NO_ACTION) {
            selectedMachinesUuids.push(machine.machineUuid);
          }
        } else if (this.selectedMachinesFirmwareMachineElevatorActions.get(machine.machineUuid) != this.NO_ACTION) {
          selectedMachinesUuids.push(machine.machineUuid);
        }
      });
    }
    return selectedMachinesUuids;
  }

  public selectVersion(firmwareType: FirmwareType): void {
    if (firmwareType === FirmwareType.FIRMWARE_IOT) {
      this.selectedMachines.forEach(machine => {
        this.updateActions(this.selectedVersionIot, machine.telemetryData.firmwareVersionIot, machine.machineUuid, this.selectedMachinesFirmwareIotActions);
      });
    } else if (firmwareType === FirmwareType.FIRMWARE_MACHINE_ECU) {
      this.selectedMachinesZitrux.forEach(machine => {
        this.updateActions(this.selectedVersionMachineEcu, machine.telemetryData.machineFirmwareVersion, machine.machineUuid,
          this.selectedMachinesFirmwareMachineEcuActions);
      });
    } else {
      this.selectedMachinesZitrux.forEach(machine => {
        this.updateActions(this.selectedVersionMachineElevator, machine.telemetryData.machineFirmwareVersionElevator,
          machine.machineUuid, this.selectedMachinesFirmwareMachineElevatorActions);
      });
    }
  }

  private getAction(firmwareVersion: string, selectedVersion: Firmware): string {
    if (firmwareVersion == null) {
      return this.UPGRADE;
    }
    let currentVersion: number = this.firmwareVersionsMap.get(firmwareVersion);
    if (currentVersion == null || currentVersion < selectedVersion.value) {
      return this.UPGRADE;
    } else if (currentVersion > selectedVersion.value) {
      return this.DOWNGRADE;
    } else {
      return this.NO_ACTION;
    }
  }

  public resetFilters(): void {
    this.currentFilters = new FirmwareFilters();
    this.totalMachines = this.machines.length;
    this.accordionMachinesSelected = true;
    this.accordionFirmwareIotSelected = false;
    this.accordionFirmwareMachineSelected = false;
    this.accordionFirmwareElevatorMachineSelected = false;
  }

  public applyFilters(): void {
    this.first = 0;
    let setSerials: Set<string> = new Set<string>();
    if (this.currentFilters.serialNumbers != null) {
      setSerials = new Set(this.currentFilters.serialNumbers);
    }
    if (this.currentFilters.machineGroups != null) {
      this.currentFilters.machineGroups.forEach(group => {
        if (group.machines != null) {
          group.machines.forEach(machine => setSerials.add(machine.serial))
        }
      });
    }
    this.filterTable(setSerials);
    this.filtersVisible = false;
  }

  private filterTable(setSerials: Set<string>): void {
    let serials: string[] = [];
    setSerials.forEach(serial => serials.push(serial));
    this.table.filter(serials, 'serial', 'in');
    this.table.filter(this.currentFilters.machineModelUuids, 'model.machineModelUuid', 'in');
    this.table.filter(this.currentFilters.organizationName?.trim(), 'organization.name', 'contains');
  }

  public selectMachine(machine: Machine): void {
    this.updateActions(this.selectedVersionIot, machine.telemetryData.firmwareVersionIot, machine.machineUuid, this.selectedMachinesFirmwareIotActions);
    if (this.isZitrux(machine)) {
      this.selectedMachinesZitrux.push(machine);
      this.updateActions(this.selectedVersionMachineEcu, machine.telemetryData.machineFirmwareVersion, machine.machineUuid,
        this.selectedMachinesFirmwareMachineEcuActions);
      this.updateActions(this.selectedVersionMachineElevator, machine.telemetryData.machineFirmwareVersionElevator,
        machine.machineUuid, this.selectedMachinesFirmwareMachineElevatorActions);
    }
  }

  public unselectMachine(machine: Machine): void {
    this.removeActions(this.selectedVersionIot, machine.machineUuid, this.selectedMachinesFirmwareIotActions);
    if (this.isZitrux(machine)) {
      this.selectedMachinesZitrux = this.selectedMachinesZitrux.filter((selectedMachineZitrux: Machine) =>
        selectedMachineZitrux.machineUuid !== machine.machineUuid);
      this.removeActions(this.selectedVersionMachineEcu, machine.machineUuid, this.selectedMachinesFirmwareMachineEcuActions);
      this.removeActions(this.selectedVersionMachineElevator, machine.machineUuid, this.selectedMachinesFirmwareMachineElevatorActions);
    }
  }

  private updateActions(selectedFirmwareVersion: Firmware, machineFirmwareVersion: string, machineUuid: string,
                        actions: Map<string, string>): void {
    if (selectedFirmwareVersion) {
      let action: string = this.getAction(machineFirmwareVersion, selectedFirmwareVersion);
      actions.set(machineUuid, action);
    }
  }

  private removeActions(selectedFirmwareVersion: Firmware, machineUuid: string, actions: Map<string, string>): void {
    if (selectedFirmwareVersion) {
      actions.delete(machineUuid);
    }
  }

  unselectMachineFromUpdate(machine: Machine): void {
    this.selectedMachines = this.selectedMachines.filter(selectedMachine => selectedMachine.machineUuid !== machine.machineUuid);
    this.unselectMachine(machine);
  }

  public selectAllRows(event: any): void {
    this.selectedMachinesZitrux = [];
    let machines = this.table.filteredValue ?? this.table.value;
    machines.forEach(machine => {
      if (event.checked) {
        this.selectMachine(machine);
      } else {
        this.unselectMachine(machine);
      }
    });
  }

  private isZitrux(machine: Machine): boolean {
    return machine.model.machineType === 'ZITRUX';
  }

  onFilter(event: any): void {
    this.totalMachines = event.filteredValue.length;
  }

  public cancel(): void {
    this.commonMessagesService.showCancelRequestConfirmation(() => {
      this.refresh();
    });
  }

  private refresh(): void {
    this.selectedVersionIot = null;
    this.selectedVersionMachineEcu = null;
    this.selectedMachines = [];
    this.selectedMachinesZitrux = [];
    this.selectedMachinesFirmwareIotActions = new Map<string, string>();
    this.selectedMachinesFirmwareMachineEcuActions = new Map<string, string>();
    this.resetFilters();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.selectedMachines.length === 0 && this.selectedVersionIot == null && this.selectedVersionMachineEcu == null
      && this.selectedVersionMachineElevator == null;
  }

  protected readonly FirmwareType = FirmwareType;

}
